"use client";

import { useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import type { GeneralSettingSection } from "@/app/(dashboard)/dashboard/webmaster/general-setting/general-setting-defaults";
import type { LogoControllerSection } from "@/app/(dashboard)/dashboard/webmaster/logo-controller/logo-controller-defaults";
import type { SwitcherLanguage } from "@/components/language-switcher";
import { Header } from "../../../components/cambodia-food-expo/sections/Header";
import { FooterSection } from "../../../components/cambodia-food-expo/sections/FooterSection";
import type { EventProgram } from "./types";
import { SectionHeading, UI, useText } from "./shared";
import { EventVideoGallery } from "./event-video-gallery";
import { EventHero } from "./event-hero";
import { EventDayNavigation } from "./event-day-nav";
import { EventIntro } from "./event-intro";
import { EventDaySection } from "./event-day-section";
import { EventSponsors } from "./event-cta";
import "./experience.css";

export function EventExperiencePage({
  program,
  generalSetting,
  languages,
  logoSection,
}: {
  program: EventProgram;
  generalSetting: GeneralSettingSection;
  languages: SwitcherLanguage[];
  logoSection: LogoControllerSection | null;
}) {
  const { text } = useText();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const lastDay = program.days[program.days.length - 1];

  return (
    // reducedMotion="user" makes every framer-motion reveal honour prefers-reduced-motion.
    <MotionConfig reducedMotion="user">
      <Header
        isScrolled={scrolled}
        data={generalSetting}
        languages={languages}
      />
      <main className="event-experience min-h-screen overflow-x-clip">
        <EventHero
          program={program}
          firstDayId={program.days[0]?.id ?? "event-overview"}
        />
        <EventIntro intro={program.intro} />
        <EventDayNavigation days={program.days} />
        {program.days.map((day) => (
          <EventDaySection
            key={day.id}
            day={day}
            isLast={day.id === lastDay?.id}
          />
        ))}
        {!!program.videos?.length && (
          <section
            id="event-video-highlights"
            aria-labelledby="event-video-highlights-title"
            className="py-16 md:py-24"
          >
            <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-6">
              <SectionHeading
                id="event-video-highlights-title"
                title={text(UI.videoHighlights)}
              />
              <EventVideoGallery
                items={program.videos}
                initial={8}
                showTitle={false}
              />
            </div>
          </section>
        )}
        <EventSponsors logoSection={logoSection} />
      </main>
      <FooterSection data={generalSetting} />
    </MotionConfig>
  );
}
